import React, { useEffect } from 'react';
import { Alert, Badge } from 'antd';
import clsx from 'clsx';
import Spin from 'src/components/common/Spin';
import { arrayWrap } from 'src/utils/arrayWrap';
import { useSearchProductsDispatchContext } from 'src/stateProviders/useSearchProductsStateContext';
import { SEARCH_PRODUCTS_ACTION_TYPES, Filters } from 'src/stateProviders/searchProductsStateProvider';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import MatchedProductsFilter from './MatchedProductsFilter';
import { CustomFilters } from './CustomFilters';
import { useAdvancedFiltersFetching } from './hooks';
import FilterWithMultiSelection from './FilterWithMultiSelection';
import { FilterOption } from './FilterWithMultiSelection/FilterWithMultiSelection';
import useSourceOwnerId from 'src/hooks/useSourceOwnerId';
import ActiveFilter from './ActiveFilter';
import NoMatchFilter from './NoMatchFilter';
import InsightsOnlyFilter from './InsightsOnlyFilter';
import AlertsOnlyFilter from './AlertsOnlyFilter';
import styles from './AdvancedFilterSider.module.less';
import { useAppStateContext } from 'src/stateProviders/useAppStateContext';

type AdvancedFilterSiderProps = {
  collapsed: boolean;
  onSelectedFiltersCount: (count: number) => void;
};

type CustomBadgeProps = {
  count: number;
};

const CustomBadge: React.FC<CustomBadgeProps> = ({ count, children }) => {
  return (
    <Badge count={count} className={clsx(count > 0 && styles.filterSelected)}>
      {children}
    </Badge>
  );
};

const transformToFilterOptions = (elem: any): FilterOption => ({
  label: elem.label.toLocaleUpperCase(),
  value: elem.Id.toString().toLowerCase(),
});

const AdvancedFilterSider: React.FC<AdvancedFilterSiderProps> = ({ collapsed, onSelectedFiltersCount }) => {
  const sourceOwnerId = useSourceOwnerId();
  const { region, matchTypeFilter, priceType } = useQueryUrlParams();
  const searchProductsDispatch = useSearchProductsDispatchContext();
  const { data, loading, error } = useAdvancedFiltersFetching(
    sourceOwnerId,
    matchTypeFilter ?? 'ALL',
    region ?? '-1',
    priceType ?? '2'
  );
  const initialBrands = data?.manufacturer?.map(transformToFilterOptions) || [];
  const initialSites = data?.owners?.map(transformToFilterOptions) || [];
  const initialPriceVariance = data?.variance?.map((type) => ({ label: type.label, value: type.Id.toString() })) || [];
  const initialMatches = data?.noOfMatches?.map((match) => ({ label: match.label, value: match.Id.toString() })) || [];

  const { brands, pricev, matches, sites, active, noMatch, alert, insight, ...otherParams } = useQueryUrlParams();
  const customFilterList = CustomFilters.extractQueryParams(otherParams);
  const { user } = useAppStateContext();
  const hasInsights = user?.appPermissions['enable_insight'];
  const hasAlerts = user?.appPermissions['enable_alert'];
  const selectedFilters = {
    active: !!active,
    noMatch: !!noMatch,
    insight: hasInsights ? !!insight : false,
    alert: hasAlerts ? !!alert : false,
    pricev: arrayWrap(pricev),
    matches: arrayWrap(matches),
    brands: arrayWrap(brands),
    sites: arrayWrap(sites),
  };

  const customFilterIds = data?.customFilterList?.map((f) => f.Id) ?? [];
  const checkedCustomFilters = Object.keys(customFilterList)
    .filter((key) => customFilterIds.includes(key))
    .map((key) => [key, customFilterList[key]]);

  const responseLoaded = !loading && (data || error);

  const handleFiltersUpdate = (key?: string, value?: boolean | unknown[]) => {
    if (!key) {
      return;
    }
    searchProductsDispatch({
      type: SEARCH_PRODUCTS_ACTION_TYPES.setSearchFilters,
      // TODO fix typings since they don't match `Filters` currently
      payload: { [key]: value } as unknown as Filters,
    });
  };

  const getCustomFilterOptions = (id: string): string[] => {
    const [, value] = checkedCustomFilters.find(([key]) => id === key) || [];
    return arrayWrap(value).filter(Boolean);
  };

  const selectedFiltersCount =
    Object.values(selectedFilters)
      .filter(Boolean)
      .filter((value) => arrayWrap(value).length > 0).length + checkedCustomFilters.length;

  useEffect(() => {
    onSelectedFiltersCount(selectedFiltersCount);
  }, [selectedFiltersCount]);

  return (
    <div className={clsx(styles.container, 'widget_color', collapsed || styles.collapsed)}>
      <div className={styles.wrapper}>
        {loading && (
          <div className={styles.loading}>
            <Spin spinning={loading} />
          </div>
        )}
        {responseLoaded &&
          (error ? (
            <div className={styles.error}>
              <Alert
                message="An error has occurred when trying to get filter list! Please try again later!"
                type="error"
                showIcon
              />
            </div>
          ) : (
            <>
              {data?.active && (
                <div className={clsx(styles.toggler, selectedFilters.active && styles.togglerActive)}>
                  <ActiveFilter defaultChecked={selectedFilters.active} onChange={handleFiltersUpdate} />
                </div>
              )}
              <div className={clsx(styles.toggler, selectedFilters.noMatch && styles.togglerActive)}>
                <NoMatchFilter
                  defaultChecked={selectedFilters.noMatch}
                  onChange={handleFiltersUpdate}
                  disabled={selectedFilters.active}
                />
              </div>
              {hasInsights && (
                <div className={clsx(styles.toggler, selectedFilters.insight && styles.togglerActive)}>
                  <InsightsOnlyFilter
                    defaultChecked={selectedFilters.insight}
                    onChange={handleFiltersUpdate}
                    disabled={false}
                  />
                </div>
              )}
              {hasAlerts && (
                <div className={clsx(styles.toggler, selectedFilters.alert && styles.togglerActive)}>
                  <AlertsOnlyFilter
                    defaultChecked={selectedFilters.alert}
                    onChange={handleFiltersUpdate}
                    disabled={false}
                  />
                </div>
              )}
              <CustomBadge count={selectedFilters.pricev.length}>
                <FilterWithMultiSelection
                  header={'Price Variance'}
                  defaultOptions={selectedFilters.pricev}
                  options={initialPriceVariance}
                  onOptionsSelected={handleFiltersUpdate}
                  keyName="pricev"
                  withSearch={false}
                  sortingSelected={false}
                  back={false}
                />
              </CustomBadge>
              <CustomBadge count={selectedFilters.matches.length}>
                <MatchedProductsFilter
                  options={initialMatches}
                  defaultValue={selectedFilters.matches}
                  onChange={handleFiltersUpdate}
                />
              </CustomBadge>
              <CustomBadge count={selectedFilters.brands.length}>
                <FilterWithMultiSelection
                  header={'Manufacturer'}
                  defaultOptions={selectedFilters.brands}
                  options={initialBrands}
                  searchPlaceholder={'Search'}
                  onOptionsSelected={handleFiltersUpdate}
                  keyName="brands"
                  back={false}
                />
              </CustomBadge>
              <CustomBadge count={selectedFilters.sites.length}>
                <FilterWithMultiSelection
                  header={'Site'}
                  searchPlaceholder={'Search'}
                  defaultOptions={selectedFilters.sites}
                  options={initialSites}
                  onOptionsSelected={handleFiltersUpdate}
                  keyName="sites"
                  back={false}
                />
              </CustomBadge>

              {data?.customFilterList?.map(({ Id, filterLabel, filterValues }) => {
                const selectedItems: string[] = getCustomFilterOptions(Id);
                return (
                  <CustomBadge count={selectedItems.length} key={Id}>
                    <FilterWithMultiSelection
                      header={filterLabel}
                      searchPlaceholder={'Search'}
                      defaultOptions={selectedItems}
                      options={filterValues}
                      onOptionsSelected={handleFiltersUpdate}
                      keyName={CustomFilters.getParamName(Id)}
                      back={false}
                    />
                  </CustomBadge>
                );
              })}
            </>
          ))}
      </div>
    </div>
  );
};

export default AdvancedFilterSider;
